import { useEffect, useState } from "react";
import { endOfDay, format, startOfMonth } from "date-fns";
import { uploadFile } from "../../../../../api/firebase/storage";
import { addFiles } from "../../../../../db/serviceFiles";
import { getByClient } from "../../../../../db/serviceAccount";
import db, { createRef } from "../../../../../db";
import * as lStorage from "../../../localstorage";
import { exportOrders } from "../services/orderExportService";
import { validateOrderExport } from "../validators/orderExportValidator";
import type { ExportAccount, ExportOption, ExportTemplate, OrderExportForm } from "../types/orderExportTypes";

const now = new Date();
const initialForm: OrderExportForm = {
  account: "", warehouse: [], status: [], fileType: "",
  fromDate: format(startOfMonth(now), "yyyy-MM-dd"), fromTime: "00:00:00",
  toDate: format(now, "yyyy-MM-dd"), toTime: format(endOfDay(now), "HH:mm:ss"), templateUrl: "",
};

export const useOrderExport = () => {
  const [form, setForm] = useState(initialForm);
  const [accounts, setAccounts] = useState<ExportAccount[]>([]);
  const [warehouses, setWarehouses] = useState<ExportOption[]>([]);
  const [templates, setTemplates] = useState<ExportTemplate[]>([]);
  const [file, setFile] = useState<File>();
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string>();
  const [resultUrl, setResultUrl] = useState<string>();
  const client = lStorage.getClientRef();
  const update = (patch: Partial<OrderExportForm>) => setForm((current) => ({ ...current, ...patch }));

  useEffect(() => {
    if (!client) return;
    getByClient(client).then((items) => setAccounts((items || []).map((account: any) => ({
      id: String(account.id),
      label: account.searchKey || account.name || String(account.id),
    }))));
    Promise.all([
      client.get(),
      db.collection("files").where("for.field", "==", "Export Order").where("client", "==", client).get(),
    ]).then(([clientSnapshot, files]) => {
      const data = clientSnapshot.data() || {};
      const values: ExportTemplate[] = [];
      if (data.exportFormatFileURL) values.push({ id: "file", name: "Export Format File", url: data.exportFormatFileURL });
      if (data.exportFormatPrepackURL) values.push({ id: "prepack", name: "Export Format Prepack", url: data.exportFormatPrepackURL });
      files.forEach((doc: any) => { if (doc.data()?.fileURL) values.push({ id: doc.id, name: doc.data().name, url: doc.data().fileURL }); });
      setTemplates(values);
    });
  }, []);

  useEffect(() => {
    if (!form.account) { setWarehouses([]); update({ warehouse: [] }); return; }
    db.collection("marketplaceWarehouseMap").where("account", "==", createRef("account", form.account)).get().then(async (query) => {
      const items = await Promise.all(query.docs.map(async (doc: any) => {
        const data = doc.data(); const pickup = await data.pickupWarehouse.get();
        return { name: pickup.data()?.name, value: data.marketplaceWarehouseId };
      }));
      const grouped = new Map<string, string[]>();
      items.forEach((item) => grouped.set(item.name, [...(grouped.get(item.name) || []), item.value]));
      setWarehouses(Array.from(grouped, ([label, values]) => ({ label, value: values.join(","), originalValues: values })));
    });
  }, [form.account]);

  const upload = async () => {
    if (!file || !client) return;
    setUploading(true);
    setError(undefined);
    try {
      const url = await uploadFile(file, `orders/${file.name}`);
      if (!url) throw new Error("The template file could not be uploaded.");
      await addFiles({ extension: file.name.split(".").pop(), fileType: file.type, fileName: file.name, fileSize: String(file.size), fileURL: url, name: file.name.replace(/\.[^.]+$/, ""), for: { menu: "order", field: "Export Order" } }, client.id);
      update({ templateUrl: url }); setFileName(file.name);
    } catch (uploadError: any) {
      setError(uploadError?.message || "The template file could not be uploaded.");
    } finally {
      setUploading(false);
    }
  };
  const submit = async () => {
    setSubmitted(true); setError(undefined);
    const validation = validateOrderExport(form);
    if (Object.values(validation).some((value) => !value)) return;
    setProcessing(true);
    const response = await exportOrders(form);
    setProcessing(false);
    if (response.statusCode === 200 && response.data.response) setResultUrl(response.data.response);
    else setError(response.data.message || response.data.error || "Export failed.");
  };
  return { form, update, accounts, warehouses, templates, file, fileName, setFile, setFileName, upload, uploading, submitted, processing, error, clearError: () => setError(undefined), resultUrl, setResultUrl, submit };
};
